import React, { useEffect } from 'react';
import { Form, Input, DatePicker, Button, Row, Col, Tooltip, Checkbox } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import SectionTitle from '../../../components/SectionTitle';
import moment from 'moment';

const { TextArea } = Input;

const AnalystProcessFsForm = ({ onFinish, initialData, loading, onValuesChange }) => {
    const [form] = Form.useForm();
    const DateFormat = 'MM-DD-YYYY';

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            const dataToSet = {
                ...initialData,
                inspectionDate: initialData.inspectionDate ? moment(initialData.inspectionDate) : null,
                invoiceDate: initialData.invoiceDate ? moment(initialData.invoiceDate) : null,
                claimDate: initialData.claimDate ? moment(initialData.claimDate) : null,
                installationDate: initialData.installationDate ? moment(initialData.installationDate) : null,
            };
            form.setFieldsValue(dataToSet);
        }
    }, [initialData, form]);

    const styles = {
        formItem: {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Subtle shadow for input fields
            borderRadius: '6px', // Rounded corners for input fields
            border: '1px solid #d9d9d9', // Light border around inputs
            padding: '8px', // Padding inside the inputs
        },
        input: {
            borderRadius: '6px', // Rounded corners for the inputs themselves
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', // Soft shadow for inputs
            border: '1px solid #d9d9d9',
        },
    };

    return (
        <Form
            className="analyst-process-fs-form"
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            scrollToFirstError
        >
            {/* ===== Top Information ===== */}
            <SectionTitle level={3}>Claim Information</SectionTitle>
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="inspectionDate" label="Inspection Date">
                        <DatePicker format={DateFormat} style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="jobName" label="Job Name">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="fsManager" label="FS Manager">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="invoiceDate" label="Invoice Date">
                        <DatePicker format={DateFormat} style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="claimNumber" label="Claim Number">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="claimDate" label="Claim Date">
                        <DatePicker format={DateFormat} style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="installationDate" label="Installation Date">
                        <DatePicker format={DateFormat} style={styles.formItem} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Contact Information ===== */}
            <SectionTitle level={3}>Contact Information</SectionTitle>
            <Row gutter={16}>
                <Col xs={24} sm={12} md={12}>
                <h6 className='mb-2'>Contact Information</h6>
                
                    <Form.Item name="contactName" label="Contact Name">
                        <Input style={styles.formItem} />
                    </Form.Item>
                    <Form.Item name="emailAddress" label="Email Address">
                        <Input style={styles.formItem} />
                    </Form.Item>
                    <Form.Item name="contactPhoneNumber" label="Contact Phone Number">
                        <Input style={styles.formItem} />
                    </Form.Item>
                    <Form.Item name="contactType" label="Contact Type">
                        <Checkbox.Group
                            options={[
                                'End-User',
                                'Dealer',
                                'Installer',
                            ]}
                        />
                    </Form.Item>

                </Col>
                <Col xs={24} sm={12} md={12}>
                   <h6 className='mb-2'>Primary Contact</h6>
                    <Form.Item name="claimReason" label="Claim Reason">
                        <Input style={styles.formItem} />
                    </Form.Item>
                    <Form.Item name="accountExec" label="Account Exec">
                        <Input style={styles.formItem} />
                    </Form.Item>
                    <Form.Item name="aePhone" label="AE Phone #">
                        <Input style={styles.formItem} />
                    </Form.Item>
                    
                </Col>
            </Row>
            {/* ===== Contact Information ===== */}
            <SectionTitle level={3}>Job Site Information</SectionTitle>
            <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="jobSiteAddress" label="Job Site Address">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="jobSiteCity" label="Job Site City">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="jobSiteState" label="Job Site State">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="jobSiteZip" label="Job Site Zip Code">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="siteInstallRelated" label="RVP">
                        <Checkbox.Group
                            options={[
                                'Site Related',
                                'Installation Related',
                            ]}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Product Information ===== */}
            <SectionTitle level={3}>Product Information</SectionTitle>
            <Row gutter={16}>

                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="orderNumber" label="Order Number">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="orderInvoice" label="Invoice">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="style" label="Style">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="color" label="Color">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="backing" label="Backing">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="jobNumber" label="Job Number">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="sqFt" label="Sq. Ft or Yards">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                    <Form.Item name="underlayment" label="Underlayment">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Warranty Information ===== */}
            <SectionTitle level={3}>Warranty Information</SectionTitle>
            <Row gutter={16}>
                <Col xs={24} sm={24}>
                    <Form.Item name="mforceManufacturing">
                        <TextArea style={styles.formItem} rows={4} />
                    </Form.Item>
                </Col>
            </Row>
            <SectionTitle level={3}>Inspector Notes</SectionTitle>
            <Row gutter={16}>
                <Col xs={24} sm={24}>
                    <Form.Item name="inspectorComplete"  label="Inspector to complete">
                        <TextArea style={styles.formItem} rows={4} />
                    </Form.Item>
                </Col>
            </Row>
            {/* ===== Type Install Section ===== */}
            <SectionTitle level={3}>Installation Details</SectionTitle>
            <Row gutter={16}>
                <Col  xs={24} sm={12} md={6}>
                    <Form.Item name="typeInstall" label="Type Install">
                    <Checkbox.Group
                        style={{ display: 'flex', flexDirection: 'column' }}
                        options={[
                        'Direct Glue',
                        'Double Glue Down',
                        'Tackless',
                        'Click',
                        'Floating',
                        'Loose Lay',
                        ]}
                    />
                    </Form.Item>
                </Col>
                <Col  xs={24} sm={12} md={6}>
                    <Form.Item name="typeGrade" label="Type Grade">
                        <Checkbox.Group
                        style={{ display: 'flex', flexDirection: 'column' }}
                            options={[
                                'Raised Access',
                                'On Grade',
                                'Above Grade',
                                'Below Grade',
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="areasAffected" label="Areas Affected">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item name="adhesiveUsed" label="Adhesive(s) Used">
                        <Input style={styles.formItem} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Site Inspection Observations ===== */}
            <SectionTitle level={3}>Site Inspection Observations</SectionTitle>
            <Form.Item name="analystNotes" label="(To be completed by Field Service Manager or Inspector)">
                <TextArea style={styles.formItem} rows={4} />
            </Form.Item>

            {/* ===== Save Button ===== */}
            <Form.Item className="form-circle-button-div">
                <Tooltip title="Save" placement="top">
                    <Button
                        type="primary"
                        loading={loading}
                        htmlType="submit"
                        shape="circle"
                        icon={<SaveOutlined className="svg-icon" />}
                        className="circle-custom-button save-report-circle-btn"
                    />
                </Tooltip>
            </Form.Item>
        </Form>
    );
};

export default AnalystProcessFsForm;